import type { Dispatch, MutableRefObject, SetStateAction } from "react";
import type React from "react";
import { useRef } from "react";
import {
    InputStyle,
    StyledChip,
    StyledTextField,
    StyledToolTip,
} from "app/pages/digital-suite/home-page/components/Rewards/screens/MainScreen/sections/InviteSection/ChipsInput/ChipsInput.styled";
import { i18nFilter } from "app/filters/i18nFilter";
import type { EmailWithError } from "app/pages/digital-suite/home-page/components/Rewards/screens/MainScreen/sections/InviteSection/utils/consts";
import { useChipsInput } from "app/pages/digital-suite/home-page/components/Rewards/screens/MainScreen/sections/InviteSection/ChipsInput/useChipsInput";
import "./ToolTip.scss";

interface IChipsInputProps {
    emailChips: string[];
    setEmailChips: Dispatch<SetStateAction<string[]>>;
    invalidEmails: EmailWithError[];
    setInvalidEmails: Dispatch<SetStateAction<EmailWithError[]>>;
    inputRef: MutableRefObject<any>;
    isDisabled?: boolean;
}

const ChipsInput = ({
    emailChips,
    setEmailChips,
    inputRef,
    invalidEmails,
    setInvalidEmails,
    isDisabled,
}: IChipsInputProps) => {
    const containerRef = useRef(null);
    const i18n = i18nFilter();

    const {
        isFocused,
        isChipsEmpty,
        shouldShowTextField,
        handleInputChange,
        handleKeyDown,
        handleChipRemove,
        handlePaste,
        isEmailInvalid,
        getTooltipContent,
        handleFocus,
        handleContainerClick,
        handleBlur,
        handleMouseDown,
        handleMouseUp,
    } = useChipsInput({
        emailChips,
        setEmailChips,
        inputRef,
        invalidEmails,
        setInvalidEmails,
    });

    const isCloseIconClick = (target: HTMLElement): boolean => {
        return !!target.closest('[data-automation-icon-name="clear-circle"]');
    };

    const handleContainerMouseDown = (e: React.MouseEvent) => {
        if (!isCloseIconClick(e.target as HTMLElement)) {
            handleMouseDown();
        }
    };

    return (
        <InputStyle
            ref={containerRef}
            className="email-chips-container"
            onFocus={handleFocus}
            onClick={handleContainerClick}
            onMouseDown={handleContainerMouseDown}
            onMouseUp={handleMouseUp}
            onBlur={handleBlur}
            isFocused={isFocused}
            isDisabled={isDisabled}
        >
            {emailChips.map((chip, index) => {
                const isChipInvalid = isEmailInvalid(chip);
                return (
                    <StyledToolTip
                        key={`${chip}_${index}`}
                        placement="left"
                        cssClass="PlainTooltip-element rewards-invite-chip-tooltip"
                        tooltipContent={i18n(getTooltipContent(chip))}
                        enabled={isChipInvalid}
                    >
                        <div>
                            <StyledChip
                                key={index}
                                text={chip}
                                onCloseItem={(e) => {
                                    e.stopPropagation();
                                    handleChipRemove(index, chip);
                                }}
                                isInvalid={isChipInvalid}
                            />
                        </div>
                    </StyledToolTip>
                );
            })}
            <StyledTextField
                ref={inputRef}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                placeholder={isChipsEmpty ? i18n("rewards_modal.main_screen.chips_input.placeholder") : null}
                hideBorder
                shouldShow={shouldShowTextField}
                hasPlaceholder={isChipsEmpty}
            />
        </InputStyle>
    );
};

export default ChipsInput;
